
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-primary hover:text-primary-foreground",
        // The `outline` variant bakes in `hover:text-primary-foreground`
        // (near-black). tailwind-merge only cancels a caller's class against
        // the SAME property+modifier, so a caller overriding `hover:bg-*`
        // keeps that near-black hover text — which is how error.tsx's "Attempt
        // Recovery" label went to ~1.06:1 on hover. This variant is the
        // supported destructive outline so the pairing cannot drift apart.
        destructiveOutline:
          "border border-destructive/60 bg-transparent text-destructive hover:bg-destructive/12 hover:text-destructive",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-primary hover:text-primary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Same 32px box the dense toolbars were hand-rolling with
        // `size="icon" className="h-8 w-8"`, but the transparent ::before
        // pushes the POINTER target back out to 44px so these stay tappable
        // one-handed on a phone without moving anything on screen.
        iconSm:
          "relative h-8 w-8 before:absolute before:-inset-1.5 before:content-['']",
        xs: "h-8 rounded-md px-2 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

    